import type { useTheme } from '@emotion/react';
import { css } from '@emotion/react';
import { commonThemeGlobals, generateGrid, commonScrollbar, globals } from 'common-ui';
import emotionNormalize from 'emotion-normalize';

export const globalcss = (theme: ReturnType<typeof useTheme>) => {
  const gridClasses = generateGrid(theme);

  //WARNING: these global styles are imported into our embedded apps which are in turn imported into ATG sites
  //Hence, any global styling here could cause issues in the ATG pages
  //Be VERY careful about declaring global styles here that aren't inside the 'appSelector' wrapped section
  return css`
    * {
      box-sizing: border-box;
    }

    html.vine-react-app,
    body.vine-react-app {
      padding: 0;
      margin: 0;
      min-height: 100%;
      font-family: ${theme.fonts.familySecondary};
      font-size: ${theme.fonts.sizeRoot};
      ${commonScrollbar(theme)};
    }

    // * '.vine-react-app' is a class used in the '<body>' of a Next.js app and embedded apps roots (header and footer).
    .vine-react-app {
      //icon fonts generated with 'iconbuild'
      @font-face {
        font-family: 'icons';
        src:
          url('${globals.assetsPath}/next/fonts/icons.ttf?v=${globals.buildVersion}') format('truetype'),
          url('${globals.assetsPath}/next/fonts/icons.woff?v=${globals.buildVersion}') format('woff'),
          url('${globals.assetsPath}/next/fonts/icons.woff2?v=${globals.buildVersion}') format('woff2');
      }
      ${emotionNormalize}
      ${commonThemeGlobals}
      ${gridClasses}

      table {
        border-collapse: collapse;
      }

      // Utility Classes
      .h1,
      h1,
      .custom-h2-header {
        ${theme.mixins.h1}
      }
      .h2,
      h2 {
        ${theme.mixins.h2}
      }
      .h3,
      h3 {
        ${theme.mixins.h3}
      }
      .h4,
      h4 {
        ${theme.mixins.h4}
      }
      small {
        font-family: ${theme.fonts.familySecondary};
        font-size: ${theme.fonts.sizeSmall};
      }
      a {
        ${theme.mixins.linkPrimary}
      }
      a.secondary {
        ${theme.mixins.linkSecondary}
      }
      a.tertiary {
        ${theme.mixins.linkTertiary}
      }
      .hxl {
        ${theme.mixins.hxl}
      }
      .h5,
      h5 {
        ${theme.mixins.h5}
      }
      .h6,
      h6 {
        ${theme.mixins.h6}
      }
      .h7 {
        ${theme.mixins.h7}
      }
      .h8 {
        ${theme.mixins.h8}
      }
      .eyebrowRegular {
        ${theme.mixins.eyebrowRegular}
      }
      .eyebrowSmall {
        ${theme.mixins.eyebrowSmall}
      }
      .utility {
        ${theme.mixins.utility}
      }
      .quote,
      blockquote {
        ${theme.mixins.quote}
      }
      .caption {
        ${theme.mixins.caption}
      }
      .body1,
      p {
        ${theme.mixins.body1}
        &.strikeThrough {
          text-decoration: ${theme.fonts.caseStrikeThrough};
        }
        &.bold {
          font-weight: ${theme.fonts.weightBold};
        }
      }
      .body2 {
        ${theme.mixins.body2}
        &.strikeThrough {
          text-decoration: ${theme.fonts.caseStrikeThrough};
        }
        &.bold {
          font-weight: ${theme.fonts.weightBold};
        }
      }
      .bodySmall {
        ${theme.mixins.body3}
        &.strikeThrough {
          text-decoration: ${theme.fonts.caseStrikeThrough};
        }
        &.bold {
          font-weight: ${theme.fonts.weightBold};
        }
      }
    }
    .enabled-vine-header {
      .aa-Panel {
        background: ${theme.colors.white};
        clip-path: inset(0 -100vmax);
        z-index: 999;
        box-shadow: 0 0 0 100vmax ${theme.colors.white};
        margin: 0;
        height: 100%;
        padding: 0 13px 67px;
        width: 100% !important;
        ${theme.breakpoints.lg} {
          height: auto;
          padding: 0;
        }
      }
      .aa-PanelLayout {
        max-height: 100%;
        flex-direction: column;
        ${theme.breakpoints.lg} {
          max-height: initial;
        }
        .view-all-items-container {
          margin-top: 1.5em;
          margin-bottom: 1.5em;
          ${theme.breakpoints.md} {
            margin-bottom: 0px;
          }
        }
      }
      .aa-SourceHeader {
        border-bottom: 0;
      }
    }
    .vine-react-app.enabled-vine-header .aa-Panel {
      width: 100% !important;
      ${theme.breakpoints.lg} {
        width: calc(100% + 700px) !important;
      }
    }
  `;
};

export default globalcss;
